/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Message, RootState, addItemToCart, useAppDispatch, updateQuantity, deleteItem, usePostCartItemMutation, useDeleteCartItemMutation, useUpdateCartItemMutation, useGetPartsZipComponentsMutation, useGenerateImageMutation } from "@circuitry-ai/doc-data";
import { checkType, CtyButton, CtyShowToast, formatPageLabels, getSelectedPartRowId, getSourceClipTimeLabel, openPreOpenedTab, PartSvgViewerInline, SourceElementType, navigateZipFileViewerWindow, type SelectedPartRow } from "@circuitry-ai/doc-ui";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'github-markdown-css/github-markdown.css';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { isNearBottom, scrollChatToBottom } from './chatAutoScroll';
import { buildMessageMetadataMap } from './messageMetadataUtils';
import { resolveAccessLevelFilterDisplay } from './accessLevelFilterDisclaimer';
import AccessLevelFilterBanner from './AccessLevelFilterBanner';

interface ChatMessageResponseProps {
    isPartsCatelogEnabled: boolean;
    msg: Partial<Message>;
    advisorId: string;
    messageId: string;
    translatedText: string;
    setInlineTriggerSource: (source: any) => void;
    setInlineTriggerLoading: (value: string) => void;
    inlineTriggerLoading: string;
    setTriggerInlineFileView: (value: boolean) => void;
    onRegenerate?: (sourceIds: string[]) => void;
    isLatestAdvisorMessage?: boolean;
}
interface CustomLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    node?: any;
    children: React.ReactNode;
    messageId?: string;
    msg?: Partial<Message>;
    setInlineTriggerSource: (source: any) => void;
    setTriggerInlineFileView: (value: boolean) => void;
    inlineTriggerLoading: string;
    setInlineTriggerLoading: (value: string) => void;
}

const PARTS_CATALOG_WRAP_COLUMNS = new Set(['Component Group', 'Component Name']);

const getTableCellText = (cell: unknown): string => {
    if (cell === null || cell === undefined) return '';
    if (typeof cell === 'string' || typeof cell === 'number') return String(cell).trim();
    if (Array.isArray(cell)) return cell.map(getTableCellText).join('').trim();
    if (React.isValidElement(cell)) return getTableCellText((cell.props as { children?: unknown }).children);
    return String(cell).trim();
};

// Finds a leaf component by name anywhere in an N-level nested component-group
// tree (groups can contain further `sub_groups`, e.g. FFPD > Divert >
// "9119956D - Tray Assembly" > "Tray Assembly"). A plain top-level scan (as this
// used to be) misses anything nested below the first level, silently falling back
// to a fake "Parts" group and breaking the parts-catalog deep link.
const findComponentByName = (
    groups: any[] | null | undefined,
    componentName: string
): { group: any; component: any } | null => {
    if (!Array.isArray(groups) || !componentName) return null;

    for (const group of groups) {
        if (Array.isArray(group?.components)) {
            const match = group.components.find(
                (component: any) => component?.name && component.name === componentName
            );
            if (match) return { group, component: match };
        }

        if (Array.isArray(group?.sub_groups) && group.sub_groups.length > 0) {
            const nested = findComponentByName(group.sub_groups, componentName);
            if (nested) return nested;
        }
    }

    return null;
};

const normalizePartKey = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const isPartsCatalogSource = (source: any): boolean =>
    source?.element_type === SourceElementType.PARTS_ZIP
    || source?.element_type === SourceElementType.PARTS_SVG;

const getZipComponentDetails = (source: any): any[] | null => {
    const details = source?.zip_components_details;
    return isPartsCatalogSource(source) && Array.isArray(details) ? details : null;
};

const isMatchingComponentDetail = (detail: any, groupKey: string, nameKey: string): boolean =>
    normalizePartKey(detail?.component_group) === groupKey
    && normalizePartKey(detail?.component_name) === nameKey;

const getDetailPartEntries = (detail: any): any[] =>
    Array.isArray(detail?.text) ? detail.text : [];

const entryHasPartNumber = (entry: any, partKey: string): boolean =>
    normalizePartKey(entry?.part_info?.parts_number) === partKey;

const entriesIncludePart = (entries: any[], partKey: string): boolean =>
    entries.some((entry: any) => entryHasPartNumber(entry, partKey));

const entriesIncludePartAndRef = (entries: any[], partKey: string, refKey: string): boolean =>
    !refKey
    || entries.some((entry: any) =>
        entryHasPartNumber(entry, partKey)
        && normalizePartKey(entry?.reference_number) === refKey
    );

const findPartsZipDetailInSource = (
    source: any,
    groupKey: string,
    nameKey: string,
    partKey: string,
    refKey: string,
): { exact?: { source: any; detail: any }; part?: { source: any; detail: any } } => {
    const details = getZipComponentDetails(source);
    if (!details) return {};

    let part: { source: any; detail: any } | undefined;
    for (const detail of details) {
        if (!isMatchingComponentDetail(detail, groupKey, nameKey)) continue;
        const entries = getDetailPartEntries(detail);
        if (!entriesIncludePart(entries, partKey)) continue;
        const match = { source, detail };
        if (entriesIncludePartAndRef(entries, partKey, refKey)) return { exact: match };
        part ??= match;
    }
    return { part };
};

/** A parts-zip source carries every component it owns under `zip_components_details`.
 *  Several catalogs can answer one message, so the clicked row has to pick its own
 *  source by component group, component name, and part number rather than the first zip. */
const findPartsZipSource = (
    sources: any[],
    componentGroup: string,
    componentName: string,
    partNumber: string,
    referenceNumber: string,
): { source: any; detail: any } | null => {
    const groupKey = normalizePartKey(componentGroup);
    const nameKey = normalizePartKey(componentName);
    const partKey = normalizePartKey(partNumber);
    const refKey = normalizePartKey(referenceNumber);
    if (!groupKey || !nameKey || !partKey) return null;

    let partMatch: { source: any; detail: any } | null = null;
    for (const source of sources) {
        const found = findPartsZipDetailInSource(source, groupKey, nameKey, partKey, refKey);
        if (found.exact) return found.exact;
        partMatch ??= found.part ?? null;
    }
    return partMatch;
};

type PartsTableField = keyof Pick<
    SelectedPartRow,
    'component_group' | 'component_name' | 'referenceNumber' | 'parts_number' | 'name' | 'quantity' | 'part_file_name'
>;

/** Parts-table header labels (lowercased, punctuation and spaces removed) per field. */
const PARTS_TABLE_HEADER_ALIASES: Record<string, PartsTableField> = {
    componentgroup: 'component_group',
    componentname: 'component_name',
    ref: 'referenceNumber',
    refno: 'referenceNumber',
    refnumber: 'referenceNumber',
    reference: 'referenceNumber',
    referenceno: 'referenceNumber',
    referencenumber: 'referenceNumber',
    partno: 'parts_number',
    partnumber: 'parts_number',
    partsnumber: 'parts_number',
    partname: 'name',
    partdescription: 'name',
    // Some answers head this column "Description" instead of "Part Name".
    description: 'name',
    qty: 'quantity',
    quantity: 'quantity',
    partfilename: 'part_file_name',
};

const normalizePartsHeader = (header: string): string =>
    header.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Reads a parts-table row by its header labels, so column order does not matter. */
const buildSelectedPartFromRow = (headers: string[], cells: unknown[]): SelectedPartRow => {
    const part: SelectedPartRow = {};
    headers.forEach((header, index) => {
        const label = normalizePartsHeader(getTableCellText(header));
        const field = PARTS_TABLE_HEADER_ALIASES[label];
        if (!field) return;
        const value = getTableCellText(cells[index]);
        // "Part Name" wins when the table also has a Description column.
        if (field === 'name') {
            if (label === 'partname' || part.name === undefined) part.name = value;
            return;
        }
        if (part[field] === undefined) part[field] = value;
    });
    // Tables without a Component Name column name the component by its group.
    part.component_name ||= part.component_group;
    return part;
};

const CustomLink = ({ node, children, messageId, msg, setInlineTriggerSource, setTriggerInlineFileView, inlineTriggerLoading, setInlineTriggerLoading, ...props }: CustomLinkProps) => {
    const href = props.href;
    if (href?.includes('ref_id=')) {
        const params = new URLSearchParams(href);
        const refId = params.get('ref_id');

        if (refId) {
            // Find the source that matches the ref_id
            const source = msg?.metaData?.[1]?.source_ids?.find((s: any) => s.ref_id == refId);
            const baseTooltip = ((source?.element_type === SourceElementType.TEXT || source?.element_type === SourceElementType.TABLE) && source?.page_labels.length > 0) ? `${source?.file_name}: ${source?.page_labels.length > 1 ? "pages" : "page"}(${formatPageLabels(source?.page_labels)})` : `${source?.file_name}`;
            // Append clip time range (e.g. "[0:11 - 0:31]") when the referenced source is a video/audio segment.
            const tooltipContent = `${baseTooltip}${getSourceClipTimeLabel(source)}`;
            if (source) {
                return (
                    <CtyButton
                        className="text-[#0BA5EC] hover:bg-content1 px-0 font-normal"
                        size="sm"
                        variant="light"
                        onPress={() => {
                            setInlineTriggerSource(source);
                            setInlineTriggerLoading(refId);
                        }}
                        isLoading={inlineTriggerLoading === refId ? true : false}
                        tooltipContent={tooltipContent}
                        toolTipColor='warning'
                    >
                        {`[${children}]`}
                    </CtyButton>
                );
            } else {
                // Source not found - prevent navigation and show error
                return (
                    <CtyButton
                        className="text-[#0BA5EC] hover:bg-content1 px-0 font-normal"
                        size="sm"
                        variant="light"
                        onPress={() => {
                            CtyShowToast(checkType.error, `Reference source not found`);
                        }}
                    >
                        {`[${children}]`}
                    </CtyButton>
                );
            }
        }
    }
    // For non-ref_id links (regular links), return standard anchor tag
    return <a {...props}>{children}</a>;
};

/**
 * Text already typed out, keyed by the Redux message object.
 *
 * Messages are rendered with their list index as the React key, so switching
 * conversations reuses the same component instance rather than remounting it —
 * a ref on the instance cannot tell "a new answer arrived" from "the user came
 * back to an answer we already showed". The message object is the stable
 * identity: streaming replaces it on every chunk, so live deltas still animate,
 * while a settled answer keeps the same object and is recognised on return.
 */
const paintedAnswers = new WeakSet<object>();

const isAdvisorRegenerateEnabled = (
    isAsk: boolean,
    clarifyingQuestionConfig: unknown,
    isPartsCatelogEnabled: boolean,
): boolean => {
    if (!isAsk || isPartsCatelogEnabled || !clarifyingQuestionConfig) return false;
    return Object.keys(clarifyingQuestionConfig as object).length > 0;
};

const hasAdvisorMessageContent = (
    processedText: string,
    accessLevelFilter: unknown,
    showRegenerateSubmit: boolean,
): boolean =>
    !!processedText.trim() || !!accessLevelFilter || showRegenerateSubmit;

const getPartsCatalogRootClassName = (isPartsCatelogEnabled: boolean, isPanelOpen: boolean): string => {
    if (!isPartsCatelogEnabled) return 'block w-full min-w-0';
    return `parts-catalog-split gap-2 w-full min-w-0 max-w-full${isPanelOpen ? ' parts-catalog-split--panel-open' : ''}`;
};

const getPartsCatalogRootStyle = (
    isPartsCatelogEnabled: boolean,
    isPanelOpen: boolean,
    panelWidth: number,
): React.CSSProperties | undefined => {
    if (!isPartsCatelogEnabled || !isPanelOpen) return undefined;
    return { '--parts-diagram-panel-width': `${panelWidth}px` } as React.CSSProperties;
};

const getPartsDiagramPaneClassName = (isPanelOpen: boolean): string =>
    `parts-catalog-split__pane parts-svg-viewer-inline h-[500px] min-w-0 max-w-full overflow-hidden relative${
        isPanelOpen ? '' : ' !hidden parts-svg-viewer-inline--hidden'
    }`;

const PartsCatalogDiagramPane = ({
    isPanelOpen,
    message,
    advisorId,
    currentActivePartsRowData,
    defaultPartsTableRowRef,
    setSelectedRowId,
    onImagePartClick,
    highlightedReferenceNumbers,
    partsRowSelectToken,
    onResizePointerDown,
    onHidePanel,
}: {
    isPanelOpen: boolean;
    message: Partial<Message>;
    advisorId: string;
    currentActivePartsRowData: any;
    defaultPartsTableRowRef: React.MutableRefObject<{ rowId: string; part: SelectedPartRow } | null>;
    setSelectedRowId: any;
    onImagePartClick: (componentDetail: SelectedPartRow) => void;
    highlightedReferenceNumbers: string[];
    partsRowSelectToken: number;
    onResizePointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;
    onHidePanel: () => void;
}) => {
    const activePartsRowData =
        currentActivePartsRowData ??
        (defaultPartsTableRowRef.current ? [defaultPartsTableRowRef.current.part] : null);

    return (
    <div className={getPartsDiagramPaneClassName(isPanelOpen)}>
        <button
            type="button"
            aria-label="Resize diagram panel"
            title="Resize"
            className="absolute left-0 top-0 z-20 flex h-full w-3 cursor-col-resize items-center justify-center border-0 bg-transparent p-0 hover:bg-primary/20"
            onPointerDown={onResizePointerDown}
        >
            <span className="flex h-10 w-3 items-center justify-center rounded-full border border-divider bg-content1 text-default-500 shadow-sm">
                <Icon icon="mdi:drag-vertical-variant" width="16" height="16" />
            </span>
        </button>
        {isPanelOpen ? (
            <button
                type="button"
                aria-label="Hide part diagram"
                title="Hide"
                onClick={onHidePanel}
                className="parts-diagram-close-overlay flex h-8 w-8 items-center justify-center rounded-md border border-divider bg-content1 text-default-500 shadow-sm hover:bg-default-100 hover:text-foreground"
            >
                <Icon icon="mdi:close" width="18" height="18" />
            </button>
        ) : null}
        <div className="parts-diagram-viewer flex-1 min-h-0 h-full">
            <PartSvgViewerInline
                message={message}
                advisorId={advisorId}
                currentActivePartsRowData={activePartsRowData}
                setSelectedRowId={setSelectedRowId}
                onImagePartClick={onImagePartClick}
                highlightedReferenceNumbers={highlightedReferenceNumbers}
                partsRowSelectToken={partsRowSelectToken}
            />
        </div>
    </div>
    );
};

const ChatMessageResponse = (props: Readonly<ChatMessageResponseProps>) => {

    const { isPartsCatelogEnabled, msg, advisorId, messageId, translatedText, setInlineTriggerSource, setTriggerInlineFileView, setInlineTriggerLoading, inlineTriggerLoading, onRegenerate, isLatestAdvisorMessage } = props;

    const [postCartItem] = usePostCartItemMutation();
    const [updateCartItem] = useUpdateCartItemMutation();
    const [deleteCartItem] = useDeleteCartItemMutation();
    const [generateImage] = useGenerateImageMutation();

    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const [isPartsImagePanelOpen, setIsPartsImagePanelOpen] = useState(isPartsCatelogEnabled);
    const [partsPanelWidth, setPartsPanelWidth] = useState(420);
    const partsCatalogSplitRef = useRef<HTMLDivElement | null>(null);
    const [currentActivePartsRowData, setCurrentActivePartsRowData] = useState<any>(null);
    const [partsRowSelectToken, setPartsRowSelectToken] = useState(0);
    const [loadingComponent, setLoadingComponent] = useState<string | null>(null);
    const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
    const [streamedText, setStreamedText] = useState<string>('');
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [imageUrlByRefId, setImageUrlByRefId] = useState<Record<string, string>>({});
    const imageUrlByRefIdRef = useRef<Record<string, string>>({});
    const failedImageRefIdsRef = useRef<Set<string>>(new Set());
    const inFlightImageRefIdsRef = useRef<Set<string>>(new Set());
    const streamingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const previousTextRef = useRef<string>('');
    const previousMessageIdRef = useRef<string>('');
    const latestTranslatedTextRef = useRef<string>('');
    const isStreamingRef = useRef<boolean>(false);
    const partsCatalogTextScrollRef = useRef<HTMLDivElement | null>(null);
    const partsCatalogAutoScrollEnabledRef = useRef(true);
    const defaultPartsTableRowRef = useRef<{ rowId: string; part: SelectedPartRow } | null>(null);
    const { itemsList, cartID } = useSelector((state: RootState) => state.globalCartSliceReducer);

    const applyPartsTableRowSelection = useCallback((part: SelectedPartRow) => {
        const rowId = getSelectedPartRowId(part);
        if (!rowId) return;
        setSelectedRowId(rowId);
        setCurrentActivePartsRowData([part]);
        setPartsRowSelectToken((prev) => prev + 1);
    }, []);

    const syncPartsTableSelectedRowId = useCallback((id: string | SelectedPartRow) => {
        const rowId = typeof id === 'string' ? id : getSelectedPartRowId(id);
        setSelectedRowId(rowId || null);
    }, []);
    
    // Extract regenerate configuration from message
    const isAsk = msg?.is_ask === true;
    const isMultiSelect = msg?.multi_select ?? true;
    const clarifyingQuestionConfig = msg?.clarifying_question_config;
    const isRegenerateEnabled = isAdvisorRegenerateEnabled(isAsk, clarifyingQuestionConfig, isPartsCatelogEnabled);

    // Extract source_items keys in order for row mapping
    const sourceItemsKeys = useMemo(() => {
        if (!isRegenerateEnabled || !clarifyingQuestionConfig?.source_items) return [];
        return Object.keys(clarifyingQuestionConfig.source_items);
    }, [isRegenerateEnabled, clarifyingQuestionConfig]);

    const dispatch = useAppDispatch();

    // Helper function to handle row selection from image click
    const handleImagePartClick = (componentDetail: SelectedPartRow) => {
        applyPartsTableRowSelection(componentDetail);
    };

    const isMarkdownTable = (text: string) => {
        const tableRegex = /\|.*\|/;
        return tableRegex.test(text);
    };

    // Extract reference numbers from the markdown table for highlighting
    const extractReferenceNumbersFromTable = (text: string): string[] => {
        if (!isMarkdownTable(text)) return [];

        const lines = text.split('\n');
        const referenceNumbers: string[] = [];

        for (const line of lines) {
            // Skip header rows and separator rows
            if (line.includes('|') && !line.includes('---') && !line.includes('| Component Name |')) {
                const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell);
                // Reference number is typically in the second column (index 1)
                if (cells.length > 1 && cells[1] && /^\d+$/.test(cells[1])) {
                    referenceNumbers.push(cells[1]);
                }
            }
        }

        return [...new Set(referenceNumbers)]; // Remove duplicates
    };

    const highlightedReferenceNumbers = extractReferenceNumbersFromTable(translatedText);

    const pinPartsCatalogTextToBottom = useCallback(() => {
        const scrollEl = partsCatalogTextScrollRef.current;
        if (!scrollEl || !partsCatalogAutoScrollEnabledRef.current) return;
        scrollEl.scrollTop = scrollEl.scrollHeight;
    }, []);

    const handlePartsCatalogTextScroll = useCallback(() => {
        const scrollEl = partsCatalogTextScrollRef.current;
        if (!scrollEl) return;
        partsCatalogAutoScrollEnabledRef.current = isNearBottom(scrollEl);
    }, []);

    // Keep the chat (and parts-catalog text pane) at the bottom while streaming
    // unless the user has scrolled up.
    const scrollMessageIntoView = useCallback((_messageId: string) => {
        scrollChatToBottom('smooth');
        pinPartsCatalogTextToBottom();
    }, [pinPartsCatalogTextToBottom]);

    // Helper function to stream words with animation
    const createStreamNextWord = (
        words: string[],
        messageId: string,
        setIsStreaming: (value: boolean) => void,
        setStreamedText: (updater: (prev: string) => string) => void,
        streamingRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
    ) => {
        let currentIndex = 0;
        
        const startTime = performance.now();
      
        // controls "speed"
        const msPerWord = 20; // ~50 words/sec
      
        const streamNextWord = () => {
          const now = performance.now();
      
          // How many words should be revealed by now?
          const targetIndex = Math.min(words.length, Math.floor((now - startTime) / msPerWord));
      
          if (targetIndex > currentIndex) {
            const chunk = words.slice(currentIndex, targetIndex).join('');
            currentIndex = targetIndex;
      
            setStreamedText(prev => prev + chunk);
      
            // keep it in view occasionally
            if (currentIndex % 25 === 0 || currentIndex === 1) {
              scrollMessageIntoView(messageId);
            }
          }
      
          if (currentIndex >= words.length) {
            if (streamingRef.current) clearTimeout(streamingRef.current);
            streamingRef.current = null;
            setIsStreaming(false);
            scrollMessageIntoView(messageId);
            return;
          }
      
          // In background tabs, timers get clamped anyway—so don’t fight it.
          // Just schedule a reasonable tick; time-based logic will catch up.
          const nextDelay = document.visibilityState === 'hidden' ? 250 : 20;
          streamingRef.current = setTimeout(streamNextWord, nextDelay);
        };
        
        return streamNextWord;
    };

    // Add the mutation hook for fetching parts zip components
    const [fetchZipComponents] = useGetPartsZipComponentsMutation();

    // Helper function to open parts catalog in new window with specific component details
    const handleOpenPartsInNewWindow = async (componentGroup: string, componentName: string, referenceNumber: string, partNumber: string, partName: string) => {
        // Prevent multiple calls by checking if already processing
        const processingKey = `${componentName}-${referenceNumber}-${partNumber}`;
        if ((globalThis as any).processingPartsCatalog?.[processingKey]) {
            console.log("Already processing this component, skipping duplicate call");
            return;
        }

        // Set processing flag
        if (!(globalThis as any).processingPartsCatalog) {
            (globalThis as any).processingPartsCatalog = {};
        }
        (globalThis as any).processingPartsCatalog[processingKey] = true;

        // Set loading state for the button
        setLoadingComponent(processingKey);

        // Get the knowledge source data from message metadata first (needed for fileName).
        // One answer can cite several parts catalogs, so the clicked row selects its own.
        const sourceIdData = msg?.metaData?.[1]?.source_ids ?? [];
        const matchedPartsZip = findPartsZipSource(
            sourceIdData,
            componentGroup,
            componentName,
            partNumber,
            referenceNumber,
        );
        // A row that doesn't resolve to one catalog still opens the first parts catalog,
        // which is what this button did before catalogs were matched per row.
        const partsZipSource = matchedPartsZip?.source ?? sourceIdData.find((source: any) =>
            source?.element_type === SourceElementType.PARTS_ZIP ||
            source?.element_type === SourceElementType.PARTS_SVG
        );

        // Pre-open a blank tab (with loading UI) synchronously to avoid popup blockers.
        const preOpened = openPreOpenedTab(partsZipSource?.file_name || 'parts-catalog');

        try {

            if (partsZipSource) {
                // First, fetch the zip components to find the matching component
                const zipComponents = await fetchZipComponents(partsZipSource.source_id).unwrap();

                // Initialize component details with clicked row information
                let selectedComponentGroup = matchedPartsZip?.detail?.component_group || "Parts";
                let selectedComponentName = matchedPartsZip?.detail?.component_name || componentName || partName;
                let selectedFileName = matchedPartsZip?.detail?.component_file_name
                    || `${componentName || partName}.png`;

                // Use clicked row details as primary source and find exact match in zip components
                if (!matchedPartsZip?.detail?.component_file_name && componentName && referenceNumber && partNumber && partName) {
                    // Search through the zip components (including nested sub_groups, since
                    // a component can be several levels deep, e.g. FFPD > Divert >
                    // "9119956D - Tray Assembly") to find an exact match by component name.
                    let foundExactMatch = false;

                    const found = findComponentByName(zipComponents, componentName);
                    if (found) {
                        selectedComponentGroup = found.group.component_name;
                        selectedComponentName = found.component.name;
                        selectedFileName = found.component.path;
                        foundExactMatch = true;
                    }

                    if (!foundExactMatch) {
                        console.log("No exact match found for component name, using clicked row details directly:", {
                            componentName: selectedComponentName,
                            fileName: selectedFileName
                        });
                    }
                } else if (!matchedPartsZip?.detail) {
                    // Fallback to first component from zip details if clicked row details are not fully available
                    if (zipComponents && Array.isArray(zipComponents) && zipComponents.length > 0) {
                        const firstGroup = zipComponents[0];
                        if (firstGroup && firstGroup.components && Array.isArray(firstGroup.components) && firstGroup.components.length > 0) {
                            const firstComponent = firstGroup.components[0];
                            selectedComponentGroup = firstGroup.component_name;
                            selectedComponentName = firstComponent.name;
                            selectedFileName = firstComponent.path;
                        }
                    }
                }

                // Create the component details for the zip file explorer - this is the key part
                // The ZipFileExplorer looks for zip_components_details[0] to auto-select the component
                const selectedComponentDetails = [{
                    component_name: selectedComponentName,
                    referenceNumber: referenceNumber,
                    parts_number: partNumber,
                    name: partName,
                    quantity: "1",
                    component_group: selectedComponentGroup,
                    file_name: selectedFileName
                }];

                // Create the source object with the selected component details
                const sourceObjectWithComponent = {
                    ...partsZipSource,
                    advisor_id: advisorId,
                    // This is what the ZipFileExplorer uses to auto-select the component
                    zip_components_details: selectedComponentDetails
                };

                // Navigate using the reusable helper function from commonutils
                const tabKey = Date.now().toString();
                const pdfData = {
                    zipSchema: zipComponents,
                    responseSourceData: sourceObjectWithComponent?.source_id,
                    fileName: sourceObjectWithComponent?.file_name ?? '',
                    fileType: 'zip',
                    sourceObject: sourceObjectWithComponent
                };

                await navigateZipFileViewerWindow(
                    preOpened,
                    pdfData,
                    advisorId,
                    sourceObjectWithComponent?.file_name,
                    tabKey
                );

                console.log("Opening parts catalog with correct component details:", {
                    clickedRow: { componentName, referenceNumber, partNumber, partName },
                    selectedComponent: {
                        componentGroup: selectedComponentGroup,
                        componentName: selectedComponentName,
                        fileName: selectedFileName
                    }
                });

                // Show success message to user
                CtyShowToast(checkType.success, `Opening ${partName} (${referenceNumber}) in new window`);
            } else {
                // Close the pre-opened window if source not found
                if (preOpened && !preOpened.closed) {
                    preOpened.close();
                }
                CtyShowToast(checkType.error, 'Parts catalog source not found');
            }
        } catch (error) {
            console.error('Error opening parts in new window:', error);
            // Close the pre-opened window on error
            if (preOpened && !preOpened.closed) {
                preOpened.close();
            }
            CtyShowToast(checkType.error, `Failed to open parts catalog: ${(error as any)?.message || 'Unknown error'}`);
        } finally {
            // Clear loading state
            setLoadingComponent(null);

            // Clear processing flag after operation completes
            setTimeout(() => {
                if ((globalThis as any).processingPartsCatalog) {
                    delete (globalThis as any).processingPartsCatalog[processingKey];
                }
            }, 1000); // Clear after 1 second to prevent any lingering locks
        }
    };

    const CustomTableRow = ({ node, ...props }: { node?: any;[x: string]: any }) => {
        const childrenArray = React.Children.toArray(props.children);
        const isHeaderRow = props.children?.[0]?.type === "th";

        // Add static property type declaration
        type CustomTableRowType = typeof CustomTableRow & {
            partsTableHeaders?: string[];
            regenerateRowCounter?: number;
        };

        // Initialize regenerate row counter if not already set
        if (isRegenerateEnabled && (CustomTableRow as CustomTableRowType).regenerateRowCounter === undefined) {
            (CustomTableRow as CustomTableRowType).regenerateRowCounter = 0;
        }
        
        // Track data row index for regenerate mapping
        // Use filename from first cell to map to source_id (more reliable than counter)
        let currentRowIndex = -1;
        if (!isHeaderRow && isRegenerateEnabled) {
            // Get filename from first cell of the row
            const firstCellContent = props.children?.[0]?.props?.children;
            let filename = typeof firstCellContent === 'string' ? firstCellContent.trim() : String(firstCellContent || '').trim();
            
            // Normalize filename: remove escape sequences (e.g., \20 becomes 20, \\ becomes \)
            filename = filename.replace(/\\(.)/g, '$1');
            
            // Find matching source_id by filename in source_items
            if (filename && clarifyingQuestionConfig?.source_items) {
                const sourceItems = clarifyingQuestionConfig.source_items as Record<string, { filename?: string }>;
                const sourceIds = Object.keys(sourceItems);
                // Find index by matching filename (try exact match first, then normalized)
                currentRowIndex = sourceIds.findIndex(sourceId => {
                    const item = sourceItems[sourceId];
                    const configFilename = item?.filename || '';
                    // Try exact match first
                    if (configFilename === filename) return true;
                    // Try normalized match (remove backslashes from config filename too)
                    const normalizedConfigFilename = configFilename.replace(/\\(.)/g, '$1');
                    return normalizedConfigFilename === filename;
                });
                
                // Fallback to counter if filename match fails
                if (currentRowIndex === -1) {
                    currentRowIndex = (CustomTableRow as CustomTableRowType).regenerateRowCounter ?? 0;
                    (CustomTableRow as CustomTableRowType).regenerateRowCounter = (currentRowIndex + 1);
                }
            } else {
                // Fallback to counter if no filename available
                currentRowIndex = (CustomTableRow as CustomTableRowType).regenerateRowCounter ?? 0;
                (CustomTableRow as CustomTableRowType).regenerateRowCounter = (currentRowIndex + 1);
            }
        }

        if (isHeaderRow && isPartsCatelogEnabled) {
            defaultPartsTableRowRef.current = null;
            if (!(CustomTableRow as CustomTableRowType).partsTableHeaders?.length) {
                (CustomTableRow as CustomTableRowType).partsTableHeaders = childrenArray
                    .map(getTableCellText)
                    .filter(Boolean);
            }
        }

        const partsTableHeaders = (CustomTableRow as CustomTableRowType).partsTableHeaders ?? [];
        const selectedPart = !isHeaderRow ? buildSelectedPartFromRow(partsTableHeaders, childrenArray) : {};

        const componentGroup = selectedPart.component_group ?? '';
        const componentName = selectedPart.component_name ?? '';
        const refNumber = selectedPart.referenceNumber ?? '';
        const partNumber = selectedPart.parts_number ?? '';
        const partName = selectedPart.name || partNumber;

        const rowId = !isHeaderRow ? getSelectedPartRowId(selectedPart) : null;
        const cartItemCode = partNumber;
        const uniqueIdentifier = `${partNumber}-${refNumber}`;

        const [inCart, setInCart] = useState(!!itemsList?.[cartItemCode]);
        const [quantity, setQuantity] = useState(itemsList?.[cartItemCode]?.totalQuanity ?? 0);

        useEffect(() => {
            setInCart(!!itemsList?.[cartItemCode]);
            setQuantity(itemsList?.[cartItemCode]?.totalQuanity ?? 0);
        }, [itemsList, cartItemCode]);

        const handleRowClick = () => {
            if (!isHeaderRow) {
                applyPartsTableRowSelection(selectedPart);
                if (isPartsCatelogEnabled) {
                    setIsPartsImagePanelOpen(true);
                }
            }
        };

        const message_id = msg?.metaData?.find((item:any)=>item?.message_id)?.message_id ?? ''
        const handleAddToCart = async () => {
            try {
                setInCart(true);
                setQuantity(1);

                const payLoad = {
                    name: partName,
                    code: partNumber,
                    type: 'part',
                    price: 0,
                    message_id,
                    sender_id: msg?.metaData?.[2]?.session_id,
                };

                const response = await postCartItem(payLoad);
                const id = response.data.items.find((item: any) => item.code === partNumber)?.id;
                const cartID = response.data.cart_id;
                const partDetails = {
                    partNumber: cartItemCode,
                    partName,
                    inCart: true,
                    id,
                    cartID,
                    quantity: 1
                };
                dispatch(addItemToCart({ partDetails, uniqueIdentifier }));

            } catch (e) {
                CtyShowToast(checkType.error, (e as Error)?.message ?? 'Operation failed');
                setInCart(false);
                setQuantity(0);
                dispatch(deleteItem({ partNumber: cartItemCode }));
            }
        };

        const incrementQuantity = async () => {
            try {
                setQuantity((prev: number) => prev + 1);
                dispatch(updateQuantity({ uniqueIdentifier, partNumber: cartItemCode, type: 'increase' }));
                const payLoad = {
                    cart_id: cartID,
                    name: partName,
                    code: partNumber,
                    type: 'part',
                    price: 0,
                };
                await updateCartItem(payLoad);
            } catch (e) {
                CtyShowToast(checkType.error, (e as Error)?.message ?? 'Operation failed');
            }
        };

        const decrementQuantity = async () => {
            try {
                setQuantity((prev: number) => prev - 1);
                dispatch(updateQuantity({ uniqueIdentifier, partNumber: cartItemCode, type: 'decrease' }));
                const payLoad = {
                    code: partNumber,
                    cartID,
                };
                await deleteCartItem(payLoad);
            } catch (e) {
                CtyShowToast(checkType.error, (e as Error)?.message ?? 'Operation failed');
            }
        };

        // Handle regenerate checkbox/radio change - capture rowIndex in closure to ensure correct value
        const handleRegenerateCheckboxChange = (rowIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            if (rowIndex >= 0 && rowIndex < sourceItemsKeys.length) {
                const sourceId = sourceItemsKeys[rowIndex];
                if (sourceId) {
                    if (isMultiSelect) {
                        if (e.target.checked) {
                            setSelectedSourceIds(prev => [...prev, sourceId]);
                        } else {
                            setSelectedSourceIds(prev => prev.filter(id => id !== sourceId));
                        }
                    } else {
                        if(e.target.checked) {
                            setSelectedSourceIds([sourceId]);
                        }
                    }
                }
            }
        };

        // Get checkbox/radio checked state
        const isRegenerateChecked = currentRowIndex >= 0 && currentRowIndex < sourceItemsKeys.length && sourceItemsKeys[currentRowIndex]
            ? selectedSourceIds.includes(sourceItemsKeys[currentRowIndex])
            : false;

        if (
            isPartsCatelogEnabled &&
            !isHeaderRow &&
            rowId &&
            !defaultPartsTableRowRef.current
        ) {
            defaultPartsTableRowRef.current = { rowId, part: selectedPart };
        }
        const effectiveSelectedRowId =
            selectedRowId ?? defaultPartsTableRowRef.current?.rowId ?? null;
        const currentRowHighlighted =
            !isHeaderRow && !!rowId && effectiveSelectedRowId === rowId;
        const isRowClickable = isPartsCatelogEnabled && !isHeaderRow;

        // Render regenerate selection cell (header or data row)
        const renderRegenerateCell = () => {
            if (!isRegenerateEnabled) return null;
            if (isHeaderRow) {
                if (isMultiSelect) {
                    return (
                        <th onClick={(e) => e.stopPropagation()}>
                            <input
                             type='checkbox'
                             checked={selectedSourceIds.length === sourceItemsKeys.length}
                             aria-disabled={!isLatestAdvisorMessage}
                             className={isLatestAdvisorMessage ? "cursor-pointer" : "cursor-not-allowed opacity-70"}
                             onChange={()=>{
                                if (!isLatestAdvisorMessage) return;
                                if (selectedSourceIds.length === sourceItemsKeys.length) setSelectedSourceIds([])
                                else setSelectedSourceIds([...sourceItemsKeys])                            
                             }}
                              />
                            </th>
                    );
                }
                return <th onClick={(e) => e.stopPropagation()}></th>;
            }
            return (
                    <td onClick={(e) => e.stopPropagation()}>
                        <input
                            type={isMultiSelect ? "checkbox" : "radio"}
                            name={isMultiSelect ? undefined : `regenerate-radio-${messageId}`}
                            checked={isRegenerateChecked}
                            onChange={(e) => {
                            if (!isLatestAdvisorMessage) {
                                e.preventDefault();
                                e.stopPropagation();
                                return;
                            }
                            handleRegenerateCheckboxChange(currentRowIndex)(e);
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isLatestAdvisorMessage) {
                                    e.preventDefault();
                                }
                            }}
                            className={isLatestAdvisorMessage ? "cursor-pointer" : "cursor-not-allowed opacity-70"}
                            aria-disabled={!isLatestAdvisorMessage}
                            key={`regenerate-${isMultiSelect ? 'checkbox' : 'radio'}-${currentRowIndex}`}
                            data-row-index={currentRowIndex}
                            data-source-id={currentRowIndex >= 0 && currentRowIndex < sourceItemsKeys.length ? sourceItemsKeys[currentRowIndex] : ''}
                        />
                    </td>
            );
        };

        return (
            <tr
                className={`custom-row select-text ${isRowClickable ? "cursor-pointer hover:bg-default-100" : ""} transition-colors duration-200 ${currentRowHighlighted ? "highlighted-row" : ""}`}
                onClick={isRowClickable ? handleRowClick : undefined}
                {...props}
            >
                {renderRegenerateCell()}
                {/* Remove the separate new tab button column */}
                {/* Modify the first column (component name) to include the new tab button */}
                {React.Children.map(childrenArray, (child, index) => {
                    if (!React.isValidElement(child)) return child;
                    const wrapClass = isPartsCatelogEnabled ? 'parts-catalog-col-wrap' : '';

                    if (index === 0 && isPartsCatelogEnabled && !isHeaderRow) {
                        // This is the first column (component name) in a data row
                        const processingKey = `${componentName}-${refNumber}-${partNumber}`;
                        const isLoading = loadingComponent === processingKey;

                        return (
                            <td {...child.props}>
                                <div className="parts-catalog-cell-content">
                                    <div className="flex items-start gap-2">
                                    <span className="min-w-0 whitespace-normal break-words">{child.props.children}</span>
                                    <span
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.stopPropagation();
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Open component in new window"
                                        className="inline-block shrink-0"
                                    >
                                        <CtyButton
                                            isIconOnly
                                            size="sm"
                                            variant="light"
                                            onPress={() => {
                                                handleOpenPartsInNewWindow(componentGroup,componentName, refNumber, partNumber, partName);
                                            }}
                                            tooltipContent="Open in new window"
                                            className="hover:bg-default-100 ml-auto"
                                            isLoading={isLoading}
                                            isDisabled={isLoading}
                                        >
                                            {!isLoading && (
                                                <Icon
                                                    icon="fluent:window-new-16-regular"
                                                    width="16"
                                                    height="16"
                                                    style={{ color: currentRowHighlighted ? "#fff" : "var(--cty-primary-accent)" }}
                                                />
                                            )}
                                        </CtyButton>
                                    </span>
                                    </div>
                                </div>
                            </td>
                        );
                    }

                    if (wrapClass) {
                        return React.cloneElement(child, {
                            children: (
                                <div className="parts-catalog-cell-content">
                                    {child.props.children}
                                </div>
                            ),
                        });
                    }

                    return child;
                })}
                {isPartsCatelogEnabled && (isHeaderRow ? (
                    <th onClick={(e) => e.stopPropagation()}>
                        <div className="parts-catalog-cell-content">Cart</div>
                    </th>
                ) : (
                    <td onClick={(e) => e.stopPropagation()}>
                        <div className="parts-catalog-cell-content">
                        {inCart ? (
                            <div className="flex items-center gap-1">
                                <CtyButton isIconOnly size="sm" variant="light" onPress={decrementQuantity}>
                                    <Icon icon="tabler:minus" width="16" height="16" className={currentRowHighlighted ? "text-white" : "text-primary"} />
                                </CtyButton>
                                <span className={`${currentRowHighlighted ? 'text-white' : 'text-primary'} font-semibold`}>{quantity}</span>
                                <CtyButton isIconOnly size="sm" variant="light" onPress={incrementQuantity}>
                                    <Icon icon="tabler:plus" width="16" height="16" className={currentRowHighlighted ? "text-white" : "text-primary"} />
                                </CtyButton>
                            </div>
                        ) : (
                            <CtyButton
                                isIconOnly
                                size="sm"
                                variant="light"
                                onPress={handleAddToCart}
                                tooltipContent="Add to Cart"
                                className="hover:bg-default-100"
                                aria-label="Add to Cart"
                            >
                                <Icon
                                    icon="tabler:shopping-cart-plus"
                                    width="18"
                                    height="18"
                                    className={currentRowHighlighted ? 'text-white' : 'text-primary'}
                                />
                            </CtyButton>
                        )}
                        </div>
                    </td>
                ))}
                
            </tr>
        );
    };


    // Function to strip markdown code block wrapper if present
    // ReDoS-safe: Uses indexOf/substring instead of vulnerable regex patterns
    const stripMarkdownCodeBlock = (text: string): string => {
        if (!text) return text;

        // Limit input size to prevent issues
        if (text.length > 100000) {
            return text;
        }

        // Check if the text starts with ```markdown and ends with ```
        if (text.startsWith('```markdown') && text.endsWith('```')) {
            const startIndex = text.indexOf('\n', 11); // After '```markdown'
            if (startIndex !== -1) {
                const endIndex = text.lastIndexOf('\n```');
                if (endIndex > startIndex) {
                    return text.substring(startIndex + 1, endIndex);
                }
            }
        }

        // Also check for generic code blocks that might contain markdown
        if (text.startsWith('```') && text.endsWith('```') && text.length > 6) {
            const startIndex = text.indexOf('\n');
            if (startIndex !== -1 && startIndex < 10) { // Reasonable limit for language identifier
                const endIndex = text.lastIndexOf('\n```');
                if (endIndex > startIndex) {
                    return text.substring(startIndex + 1, endIndex);
                }
            }
        }

        return text;
    };

    const imagePlaceholderRegex = /\[IMAGE\]\(img_ref_id=([^)]+)\)/g;

    const extractImageRefIds = (text: string): string[] => {
        if (!text) return [];
        const matches = text.matchAll(imagePlaceholderRegex);
        return Array.from(matches, (match) => match[1]?.trim()).filter(Boolean);
    };

    const convertImagePlaceholdersToMarkdownImages = (text: string): string => {
        if (!text) return text;
        // Use an absolute URL token so markdown keeps src reliably; encode to support special chars.
        return text.replace(imagePlaceholderRegex, (_match, refId: string) => `![IMAGE](https://cty-image-ref.local/${encodeURIComponent(refId.trim())})`);
    };

     // Function to process markdown text while preserving table formatting
     const processMarkdownText = (text: string): string => {
        if (!text) return text;
        const processedText = stripMarkdownCodeBlock(text);
        return processedText.split('\n').map(line => {
            const trimmed = line.trim();
            // Normalize table rows by removing leading whitespace (markdown tables need to start at column 0)
            if (trimmed.startsWith('|') && trimmed.includes('|') && trimmed.length > 1) {
                // Remove trailing whitespace before final pipe using safe string manipulation (avoid ReDoS)
                const trimmedEnd = trimmed.trimEnd();
                if (trimmedEnd.endsWith('|')) {
                    return trimmedEnd;
                }
                // If pipe was removed by trimEnd, add it back
                return trimmedEnd + '|';
            }
            // For non-table lines, add two spaces at the end for markdown line breaks
            return trimmed.length > 0 ? line + '  ' : line;
        }).join('\n');
    };
    // Keep refs in sync for visibility handler
    latestTranslatedTextRef.current = translatedText ?? '';
    isStreamingRef.current = isStreaming;

    useEffect(() => {
        const onVisibilityChange = () => {
            // Only snap to the final text and scroll when the tab is hidden
            // mid-stream. If nothing is streaming (e.g. the user is just
            // reading a finished message), losing focus shouldn't move the
            // viewport at all.
            if (document.visibilityState !== 'hidden' || !isStreamingRef.current) return;

            if (streamingRef.current) {
                clearTimeout(streamingRef.current);
                streamingRef.current = null;
            }
            const latest = latestTranslatedTextRef.current;
            if (latest) {
                setStreamedText(latest);
                previousTextRef.current = latest;
            }
            setIsStreaming(false);
            // Non-forced scroll: respects chatAutoScrollEnabledRef so a user
            // who scrolled up to read from the beginning stays put.
            scrollChatToBottom('smooth');
            pinPartsCatalogTextToBottom();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [pinPartsCatalogTextToBottom]);

    // Word-by-word streaming effect - only for advisor messages
    useEffect(() => {
        // Animate only while the answer is actively arriving. Completed answers
        // (finished turns re-mounted on conversation switch, or history) must
        // paint in full so the same reply never types itself out twice.
        // Answers restored from history are already settled, and any answer this
        // client has already typed out must not type itself out again when the
        // user comes back to the conversation. Everything else animates, so a
        // reply delivered in one response still reads as if it were streaming.
        //
        // Whether the object was painted, not what it was painted with: the
        // parent holds the translated text in its own state and resets it to ''
        // while switching conversations, so comparing text would read a settled
        // answer as new on that empty pass and type the whole reply out again.
        // Streaming still animates because each chunk yields a new message
        // object, which this has never seen.
        const settled = msg?.isHistorical || (!!msg && paintedAnswers.has(msg as object));

        if (!msg?.isAdvisor || settled) {
            // A queued tick would append onto the text we are about to settle.
            if (streamingRef.current) {
                clearTimeout(streamingRef.current);
                streamingRef.current = null;
            }
            setStreamedText(translatedText || '');
            setIsStreaming(false);
            previousTextRef.current = translatedText || '';
            previousMessageIdRef.current = messageId;
            return;
        }

        // Recorded before animating, not after, so switching away mid-answer and
        // returning shows the finished text instead of restarting the typing.
        // Only once there is something to paint, or the empty pass above would
        // mark an answer as done before it ever appeared.
        if (msg && translatedText?.trim()) paintedAnswers.add(msg as object);

        // Clear any existing streaming
        if (streamingRef.current) {
            clearTimeout(streamingRef.current);
            streamingRef.current = null;
        }

        // If translatedText is empty, reset streaming
        if (!translatedText || translatedText.trim() === '') {
            setStreamedText('');
            setIsStreaming(false);
            previousTextRef.current = '';
            previousMessageIdRef.current = '';
            return;
        }

        // Check if this is a completely new message (different messageId or text doesn't continue from previous)
        // This ensures streaming works for ALL consecutive messages, not just the first one
        const isNewMessage = previousMessageIdRef.current !== messageId || 
                            previousTextRef.current === '' || 
                            !translatedText.startsWith(previousTextRef.current) || 
                            translatedText.length < previousTextRef.current.length;
        
        if (isNewMessage) {
            // Reset everything for new message
            previousMessageIdRef.current = messageId;
            previousTextRef.current = '';
            setStreamedText('');
            // Start streaming from the beginning
            setIsStreaming(true);
            partsCatalogAutoScrollEnabledRef.current = true;
            defaultPartsTableRowRef.current = null;
            if (isPartsCatelogEnabled) {
                setSelectedRowId(null);
                setCurrentActivePartsRowData(null);
                setPartsRowSelectToken(0);
                setIsPartsImagePanelOpen(true);
            }
            setStreamedText('');
            
            // Split text into words (preserving spaces and newlines)
            const words = translatedText.match(/\S+|\s+/g) || [];
            const streamNextWord = createStreamNextWord(
                words,
                messageId,
                setIsStreaming,
                setStreamedText,
                streamingRef
            );

            // Start streaming
            streamNextWord();
        } else if (translatedText.length > previousTextRef.current.length) {
            // Continue streaming if text is still being added
            const remainingText = translatedText.slice(previousTextRef.current.length);
            previousTextRef.current = translatedText;
            const words = remainingText.match(/\S+|\s+/g) || [];
            const streamNextWord = createStreamNextWord(
                words,
                messageId,
                setIsStreaming,
                setStreamedText,
                streamingRef
            );

            streamNextWord();
        } else {
            // Text is complete, stop streaming
            setStreamedText(translatedText);
            previousTextRef.current = translatedText;
            setIsStreaming(false);
        }

        // Update previous text ref when streaming completes or text changes
        if (!isStreaming && streamedText === translatedText) {
            previousTextRef.current = translatedText;
        }

        // Cleanup on unmount or when translatedText/messageId changes
        return () => {
            if (streamingRef.current) {
                clearTimeout(streamingRef.current);
                streamingRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translatedText, messageId, msg?.isHistorical]);

    // Process the streamed text (or full text if streaming is complete)
    const textToDisplay = isStreaming ? streamedText : translatedText;
    const metadataMap = buildMessageMetadataMap(msg?.metaData);
    const accessLevelFilter = resolveAccessLevelFilterDisplay(
        textToDisplay,
        metadataMap.filters
    );
    const textForMarkdown = accessLevelFilter?.messageText ?? textToDisplay;
    const processedText = processMarkdownText(textForMarkdown);
    const processedTextWithImageTokens = convertImagePlaceholdersToMarkdownImages(processedText);

    // The parts-catalog markdown lives in a fixed 500px overflow pane, so the
    // outer chat scroller cannot follow new words. Pin this inner pane to the
    // bottom while streaming, and stop if the user scrolls it up.
    useEffect(() => {
        if (!isPartsCatelogEnabled) return undefined;
        const scrollEl = partsCatalogTextScrollRef.current;
        const contentEl = scrollEl?.firstElementChild;
        const frameId = requestAnimationFrame(() => {
            pinPartsCatalogTextToBottom();
        });
        if (!contentEl) {
            return () => cancelAnimationFrame(frameId);
        }
        const observer = new ResizeObserver(() => {
            if (!isStreamingRef.current) return;
            pinPartsCatalogTextToBottom();
        });
        observer.observe(contentEl);
        return () => {
            cancelAnimationFrame(frameId);
            observer.disconnect();
        };
    }, [isPartsCatelogEnabled, isStreaming, streamedText, pinPartsCatalogTextToBottom]);

    useEffect(() => {
        imageUrlByRefIdRef.current = {};
        failedImageRefIdsRef.current = new Set();
        inFlightImageRefIdsRef.current = new Set();
        setImageUrlByRefId({});
    }, [messageId]);

    useEffect(() => {
        // Wait until text streaming finishes so all image placeholders are present
        if (isStreaming) return;

        const imageRefIds = extractImageRefIds(translatedText || '');
        if (imageRefIds.length === 0) return;

        const unresolvedRefIds = imageRefIds.filter(
            (refId) =>
                !imageUrlByRefIdRef.current[refId] &&
                !failedImageRefIdsRef.current.has(refId) &&
                !inFlightImageRefIdsRef.current.has(refId)
        );

        if (unresolvedRefIds.length === 0) return;

        let cancelled = false;

        unresolvedRefIds.forEach((refId) => {
            inFlightImageRefIdsRef.current.add(refId);
        });

        unresolvedRefIds.forEach((refId) => {
            void (async () => {
                try {
                    const imageResponse = await generateImage({
                        s3_paths: [refId],
                    }).unwrap();

                    if (cancelled) return;

                    const resolvedItem = (imageResponse?.data || []).find(
                        (item: { s3_path: string; presigned_url: string }) => item?.s3_path === refId
                    ) ?? imageResponse?.data?.[0];

                    if (resolvedItem?.s3_path && resolvedItem?.presigned_url) {
                        imageUrlByRefIdRef.current = {
                            ...imageUrlByRefIdRef.current,
                            [resolvedItem.s3_path]: resolvedItem.presigned_url,
                        };
                        setImageUrlByRefId({ ...imageUrlByRefIdRef.current });
                    } else {
                        failedImageRefIdsRef.current.add(refId);
                    }
                } catch (error) {
                    if (cancelled) return;
                    console.error('Error fetching image URL:', error);
                    failedImageRefIdsRef.current.add(refId);
                } finally {
                    inFlightImageRefIdsRef.current.delete(refId);
                }
            })();
        });

        return () => {
            cancelled = true;
        };
    }, [translatedText, generateImage, isStreaming]);
    
    // Reset row index counter synchronously before each render (ensures proper mapping on re-renders)
    if (isRegenerateEnabled) {
        type CustomTableRowType = typeof CustomTableRow & { 
            regenerateRowCounter?: number;
        };
        (CustomTableRow as CustomTableRowType).regenerateRowCounter = 0;
    }

    // Check if markdown contains a table
    const hasTable = isMarkdownTable(translatedText);

    // Handle regenerate button click
    const handleRegenerateClick = () => {
        if (onRegenerate && selectedSourceIds.length > 0) {
            onRegenerate(selectedSourceIds);
        }
    };

    const startPartsPanelResize = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const splitEl = partsCatalogSplitRef.current;
        if (!splitEl) return;
        const startX = event.clientX;
        const startWidth = partsPanelWidth;
        const splitWidth = splitEl.getBoundingClientRect().width;

        const onMove = (moveEvent: PointerEvent) => {
            const next = startWidth + (startX - moveEvent.clientX);
            const min = 240;
            const max = Math.max(min, Math.round(splitWidth * 0.7));
            setPartsPanelWidth(Math.min(max, Math.max(min, next)));
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    }, [partsPanelWidth]);

    const showRegenerateSubmit = isRegenerateEnabled && hasTable && selectedSourceIds.length > 0;
    const hasContent = hasAdvisorMessageContent(
        processedTextWithImageTokens ?? '',
        accessLevelFilter,
        showRegenerateSubmit,
    );
    
    return (
        <div
            ref={isPartsCatelogEnabled ? partsCatalogSplitRef : undefined}
            className={getPartsCatalogRootClassName(isPartsCatelogEnabled, isPartsImagePanelOpen)}
            style={getPartsCatalogRootStyle(isPartsCatelogEnabled, isPartsImagePanelOpen, partsPanelWidth)}
        >
            {hasContent && (
                <div className={isPartsCatelogEnabled ? 'parts-catalog-split__pane min-w-0 max-w-full overflow-hidden relative' : undefined}>
                    {isPartsCatelogEnabled && !isPartsImagePanelOpen && (
                        <button
                            type="button"
                            aria-label="Show part diagram"
                            title="Show part diagram"
                            onClick={() => setIsPartsImagePanelOpen(true)}
                            className="absolute top-1.5 right-4 z-10 flex h-8 w-8 items-center justify-center rounded-md border border-divider bg-content1 text-primary shadow-sm hover:bg-primary hover:text-primary-foreground"
                        >
                            <Icon icon="mdi:image-outline" width="18" height="18" />
                        </button>
                    )}
                    <div
                        ref={isPartsCatelogEnabled ? partsCatalogTextScrollRef : undefined}
                        className={isPartsCatelogEnabled ? 'h-full min-w-0 max-w-full overflow-y-auto overflow-x-auto parts-catalog-table-scroll' : undefined}
                        onScroll={isPartsCatelogEnabled ? handlePartsCatalogTextScroll : undefined}
                    >
                        <span
                            id={messageId}
                            className={`markdown-body${isPartsCatelogEnabled ? ' parts-catalog-markdown' : ''}`}
                            style={{
                                color: 'black',
                                backgroundColor: 'transparent',
                                display: isPartsCatelogEnabled ? 'block' : 'inline-block',
                                maxWidth: '100%',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                            }}
                        >
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                ul: ({ node, ...props }) => <ul className="list-disc" {...props} />,
                                ol: ({ node, ...props }) => <ol className="list-decimal" {...props} />,
                                table: ({ node, children, ...props }) => (
                                    <div className={`my-2 max-w-full min-w-0${isPartsCatelogEnabled ? '' : ' overflow-x-auto'}`}>
                                        <table
                                            className={`border-collapse border border-default-300 ${
                                                isPartsCatelogEnabled ? 'parts-catalog-table' : '!w-full'
                                            }`}
                                            {...props}
                                        >
                                            {children}
                                        </table>
                                    </div>
                                ),
                                tr: (isPartsCatelogEnabled || isRegenerateEnabled) ? CustomTableRow : ({ node, ...props }) => <tr {...props} />,
                                a: (props: any) => (
                                    <CustomLink
                                        {...props}
                                        messageId={messageId}
                                        msg={msg}
                                        setInlineTriggerSource={setInlineTriggerSource}
                                        setTriggerInlineFileView={setTriggerInlineFileView}
                                        inlineTriggerLoading={inlineTriggerLoading}
                                        setInlineTriggerLoading={setInlineTriggerLoading}
                                    >
                                        {props.children}
                                    </CustomLink>
                                ),
                                img: ({ node, src, alt, ...props }) => {
                                    const imageRefPrefix = 'https://cty-image-ref.local/';
                                    const source = typeof src === 'string' ? src.trim() : '';
                                    const isImageRefPlaceholder = source.startsWith(imageRefPrefix);
                                    if (!isImageRefPlaceholder) {
                                        if (!source) {
                                            return null;
                                        }
                                        return (
                                            <img
                                                src={source}
                                                alt={alt || 'image'}
                                                className="max-w-full h-auto rounded-md my-2"
                                                {...props}
                                            />
                                        );
                                    }

                                    const encodedRefId = source.replace(imageRefPrefix, '');
                                    const refId = decodeURIComponent(encodedRefId);
                                    const imageUrl = imageUrlByRefId[refId];
                                    if (!imageUrl) {
                                        return null;
                                    }

                                    return (
                                        <img
                                            src={imageUrl}
                                            alt={alt || 'image'}
                                            className="max-w-full h-auto rounded-md my-2"
                                            {...props}
                                        />
                                    );
                                },
                            }}>
                                {processedTextWithImageTokens?.replace(/\n/g, '  \n')}
                            </ReactMarkdown>
                        </span>
                        {accessLevelFilter && (
                            <AccessLevelFilterBanner
                                accessLevelCode={accessLevelFilter.accessLevelCode}
                                filterMessage={accessLevelFilter.filterMessage}
                            />
                        )}
                        {/* Regenerate button - show below table when source_ids are selected */}
                        {showRegenerateSubmit && (
                            <div className="mt-3 flex justify-start">
                                <CtyButton
                                    size="sm"
                                    variant="solid"
                                    color="primary"
                                    onPress={handleRegenerateClick}
                                    isDisabled={!isLatestAdvisorMessage}
                                >
                                    Submit
                                </CtyButton>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {isPartsCatelogEnabled ? (
                <PartsCatalogDiagramPane
                    isPanelOpen={isPartsImagePanelOpen}
                    message={msg}
                    advisorId={advisorId}
                    currentActivePartsRowData={currentActivePartsRowData}
                    defaultPartsTableRowRef={defaultPartsTableRowRef}
                    setSelectedRowId={syncPartsTableSelectedRowId}
                    onImagePartClick={handleImagePartClick}
                    highlightedReferenceNumbers={highlightedReferenceNumbers}
                    partsRowSelectToken={partsRowSelectToken}
                    onResizePointerDown={startPartsPanelResize}
                    onHidePanel={() => setIsPartsImagePanelOpen(false)}
                />
            ) : null}
        </div>
    );

};

export { ChatMessageResponse };